import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DailyChatsChart = ({ chats }) => {
  const dailyChatsData = useMemo(() => {
    const days = {};
    const currentDate = new Date();
    
    // Inicializar últimos 15 días
    for (let i = 14; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() - i);
      const key = date.toISOString().split('T')[0]; // YYYY-MM-DD
      days[key] = 0;
    }

    // Contar chats nuevos por día (basado en createdAt)
    chats.forEach(chat => {
      if (chat.createdAt) {
        const chatDate = chat.createdAt?.toDate ? chat.createdAt.toDate() : new Date(chat.createdAt);
        const key = chatDate.toISOString().split('T')[0];
        
        if (days.hasOwnProperty(key)) {
          days[key]++;
        }
      }
    });

    return days;
  }, [chats]);

  const data = {
    labels: Object.keys(dailyChatsData).map(key => {
      const date = new Date(key);
      return date.toLocaleDateString('es-ES', { 
        month: 'short', 
        day: 'numeric' 
      });
    }),
    datasets: [
      {
        label: 'Nuevos Chats',
        data: Object.values(dailyChatsData),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgba(75, 192, 192, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(75, 192, 192, 1)',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 10,
        right: 10,
      },
    },
    plugins: {
      legend: {
        position: 'top',
        align: 'start',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: function(context) {
            return `Nuevos chats: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
          borderDash: [2, 2],
        },
        ticks: {
          font: {
            size: 11,
            weight: '500',
          },
          color: '#666',
          maxRotation: 45,
        },
        title: {
          display: false,
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
          borderDash: [2, 2],
        },
        ticks: {
          precision: 0,
          font: {
            size: 11,
            weight: '500',
          },
          color: '#666',
        },
        title: {
          display: false,
        }
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    hover: {
      mode: 'nearest',
      intersect: true,
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
        borderWidth: 2,
      },
      line: {
        borderWidth: 3,
      },
    },
  };

  return <Line data={data} options={options} />;
};

export default DailyChatsChart;
